import { useState, useEffect } from "react";
import { Stack, Typography, Box, Container, Link, ButtonGroup, Button } from "@mui/material";
import { useMutation, useQuery } from "@apollo/client";
import Timeline from "@mui/lab/Timeline";
import "dayjs/locale/en-in";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Image } from "mui-image";
import Switch from '@mui/material/Switch';

import { TraceV2Input } from "../types";
import CustomerEngagement from "./CustomerEngagement";
import { CreateConsumerScan } from "../graphql/mutation";
import { GetFreshnessMeter } from "../graphql/queries"
import ContentSlider from "./Carousels/ContentSlider";
import { generateSessionId, storeSessionId, getSessionId } from "../utils";
import TimeLine from "./Common/TimeLine";
import { splitAndGroupTraces, getFormattedUrl, groupTracesByLocation } from "../utils";
import MapView from "./MapView/map";
import { MAP_VIEW, TIMELINE_VIEW } from "../constants";
import { TraceDataProvider } from "../context/TraceDataContext";
import EventDetails from "./Common/EventDetails";
import { useConsumerEngagement } from "../Hooks/useConsumerEngagement";
import EmptyTrace from "../components/UI/EmptyTrace";

dayjs.extend(localizedFormat);
dayjs.locale("en-in");

const theme = createTheme({
    palette: {
        primary: {
            main: "#ffffff",
        },
    },
    typography: {
        h6: {
            fontWeight: "bold",
        },
    },
});

function TraceV2(props: TraceV2Input) {
    const { orgInfo, traces, traceItemID, orgId } = props;
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [open, setOpen] = useState<boolean>(false);
    const [selectedEventData, setSelectedEventData] = useState<any>(null);
    const [selectedProductDetails, setSelectedProductDetails] = useState<Record<string, any> | null>(null);
    const { consumerEngagementDetails, loading, isExpired } = useConsumerEngagement({ orgId, traceItemId: traceItemID });
    const [tracesWithFreshnessMeter, setTracesWithFreshnessMeter] = useState(traces);

    // Group traces for timeline view (by userEvent)
    const orderedTraces = splitAndGroupTraces(tracesWithFreshnessMeter);

    // Group traces for map view (by location)
    const groupedLocationsList = groupTracesByLocation(tracesWithFreshnessMeter);

    const [checked, setChecked] = useState(false);

    // Get viewer options from orgInfo, default to both views if not provided
    const viewerOptions = orgInfo?.viewerOptions || [TIMELINE_VIEW, MAP_VIEW];
    const defaultViewer = orgInfo?.defaultViewer || TIMELINE_VIEW;

    // Initialize viewMode based on defaultViewer, fallback to first available option
    const initialViewMode = viewerOptions.includes(defaultViewer)
        ? defaultViewer
        : viewerOptions[0] || TIMELINE_VIEW;

    const [viewMode, setViewMode] = useState<'timeline' | 'map'>(initialViewMode as 'timeline' | 'map');

    // Determine which views are available
    const showTimeline = viewerOptions.includes(TIMELINE_VIEW);
    const showMap = viewerOptions.includes(MAP_VIEW);
    const showViewToggle = viewerOptions.length > 1;

    // Only reserve space for the org-info card when it actually has something
    // to show (organization slide content, or the customer engagement slide).
    const hasOrgDetails = Boolean(
        orgInfo?.logoPresignedUrl ||
        orgInfo?.tagLine ||
        orgInfo?.storyLine ||
        (orgInfo?.organizationCertificates && orgInfo.organizationCertificates.length > 0)
    );
    const hasCustomerEngagement = Boolean(
        !isExpired &&
        !loading &&
        (consumerEngagementDetails?.region?.engagementImage || consumerEngagementDetails?.region?.engagementMessage)
    );
    const showOrgInfoBox = hasOrgDetails || hasCustomerEngagement;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setChecked(event.target.checked);
    };

    const handleViewModeChange = (mode: 'timeline' | 'map') => {
        setViewMode(mode);
    };

    const getButtonStyles = (isActive: boolean, extraPadding?: boolean) => ({
        backgroundColor: isActive ? '#0073e6' : 'transparent',
        color: isActive ? 'white' : '#0073e6',
        borderColor: '#0073e6',
        '&:hover': {
            backgroundColor: isActive ? '#005bb5' : '#f0f8ff',
        },
        ...(extraPadding && { px: 3 })
    });

    // View mode configuration
    const viewModes = [
        { key: TIMELINE_VIEW, label: 'Timeline View' },
        { key: MAP_VIEW, label: 'Map View', extraPadding: true }
    ];

    // Mutation to get consumer engeagement details
    const [CreateConsumerEngagement] = useMutation(CreateConsumerScan);

    // Query to get freshness meter details
    const { data: freshnessData } = useQuery(GetFreshnessMeter, {
        variables: {
            filters: {
                "epicsEventId": traces[0]?.eventID
            }
        },
        skip: !traces[0]?.eventID,
    });

    // Update traces with freshness meter data when fetched
    useEffect(() => {
        if (freshnessData?.getFreshnessMeter && freshnessData.getFreshnessMeter.length > 0) {
            const updatedTraces = traces.map((trace, index) => ({
                ...trace,
                freshnessMeterDetails: index === 0 ? freshnessData.getFreshnessMeter[0] : null
            }));
            setTracesWithFreshnessMeter(updatedTraces);
        } else {
            setTracesWithFreshnessMeter(traces);
        }
    }, [freshnessData, traces]);

    // Check Session ID and send session details to backend
    useEffect(() => {
        let sessionId = getSessionId(traceItemID);
        if (!sessionId) {
            sessionId = generateSessionId();
            storeSessionId(traceItemID, sessionId);
        }
        setSessionId(sessionId);

        const { timeZone } = Intl.DateTimeFormat().resolvedOptions();


        if (!traceItemID || loading) return;

        CreateConsumerEngagement({
            variables: {
                consumerScanInput: {
                    sessionId: sessionId,
                    timeZone: timeZone,
                    // ...(consumerEngagementDetails?.id && { consumerEngagementDefId: String(consumerEngagementDetails.id) }),
                    traceItemId: traceItemID,
                    orgId
                }
            }
        }).then(response => {
            console.log(response);
        }).catch(error => {
            console.log(error);
        });
    }, [traceItemID, loading]);


    return (
        <ThemeProvider theme={theme}>
            <Stack direction="column" justifyContent="center" alignItems="center" spacing={0} sx={{ width: "100%", maxWidth: "100%", padding: 0 }}>
                {orgInfo?.bannerPresignedUrl && (
                    <Stack direction="column" justifyContent="center" alignItems="center"
                        sx={{ height: { sm: "150px", md: "175px", lg: "250px" }, }}>
                        <Image
                            src={orgInfo.bannerPresignedUrl}
                            alt="banner"
                            sx={{
                                objectFit: "contain",
                                width: "100%",
                                height: { sm: "150px", md: "175px", lg: "250px" },
                            }}
                        />
                    </Stack>
                )}

                {showOrgInfoBox && (
                    <Box
                        maxWidth={{ xs: "100%", md: "800px" }}
                        id="org-info"
                        sx={{ py: 1, px: 1, backgroundColor: "#FFFFFF", borderRadius: "8px", boxShadow: 3, mt: 2, alignItems: "center", }}
                    >
                        <ContentSlider
                            orgInfo={orgInfo}
                            customerScanDetails={consumerEngagementDetails}
                            isExpired={isExpired}
                            loading={loading}
                        />
                    </Box>
                )}

                {!isExpired && !loading &&
                    <Container maxWidth="md" id="consumer-info" sx={{ alignItems: "center", display: "flex", alignContent: "center", flexDirection: "column" }}>
                        <CustomerEngagement customerScanDetails={consumerEngagementDetails} sessionId={sessionId} traceItemID={traceItemID} />
                    </Container>
                }
                <Typography variant="h5" sx={{ mt: 2 }}>Story of Your Product</Typography>

                {orderedTraces && orderedTraces?.length <= 0 && (
                    <EmptyTrace isTrace />
                )}

                {/* View Mode Toggle - Only show if multiple viewer options available */}
                {orderedTraces?.length > 0 && showViewToggle && (
                    <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                        <ButtonGroup variant="outlined">
                            {viewModes
                                .filter(({ key }) => viewerOptions.includes(key))
                                .sort((a, b) => (a.key === defaultViewer ? -1 : b.key === defaultViewer ? 1 : 0))
                                .map(({ key, label, extraPadding }) => (
                                    <Button
                                        key={key}
                                        onClick={() => handleViewModeChange(key as 'timeline' | 'map')}
                                        variant={viewMode === key ? 'contained' : 'outlined'}
                                        sx={getButtonStyles(viewMode === key, extraPadding)}
                                    >
                                        {label}
                                    </Button>
                                ))}
                        </ButtonGroup>
                    </Box>
                )}

                {/* Timeline View Controls - Only show in timeline mode */}
                {showTimeline && viewMode === 'timeline' && orderedTraces?.length > 1 &&
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignContent: "center", mt: 1 }}>
                        <Typography fontSize={20} color={"gray"} mr={1} mt={0.5}>View in reverse order</Typography>
                        <Switch
                            checked={checked}
                            color="info"
                            size="medium"
                            onChange={handleChange}
                            inputProps={{ 'aria-label': 'controlled' }}
                        />
                    </Box>
                }

                {/* Timeline View - Only show when timeline mode is selected and timeline is available */}
                {showTimeline && viewMode === TIMELINE_VIEW && (
                    <TraceDataProvider>
                        <Timeline sx={{ px: { xs: 1, sm: 2 } }}>
                            {(checked ? [...orderedTraces].reverse() : orderedTraces).map((item, idx) => {
                                const displayedTraces = checked ? [...orderedTraces].reverse() : orderedTraces;
                                const isLastInDisplay = idx === displayedTraces.length - 1;
                                const actualLastItemIndex = orderedTraces.length - 1;
                                const isActualLastItem = item.originalIndex === orderedTraces[actualLastItemIndex].originalIndex;

                                // For grouped traces, use the first item; for single, use the data directly
                                const isGrouped = item.type === 'group';
                                const traceData = isGrouped ? item.data[0] : item.data;
                                const groupedTraces = isGrouped ? item.data : [];

                                return (
                                    <TimeLine
                                        key={`${item.type}-${item.originalIndex}`}
                                        traces={[traceData]}
                                        isLastInTimeline={isActualLastItem}
                                        isLastInDisplay={isLastInDisplay}
                                        isGrouped={isGrouped}
                                        groupedTraces={groupedTraces}
                                        onMoreDetails={(payload) => {
                                            setOpen(true);
                                            setSelectedEventData(payload?.eventData ?? null);
                                            setSelectedProductDetails(payload?.productDetails ?? null);
                                        }}
                                    />
                                );
                            })}
                        </Timeline>
                    </TraceDataProvider>
                )}
                {open &&
                    <EventDetails
                        open={open}
                        onClose={() => {
                            setOpen(false);
                            setSelectedEventData(null);
                            setSelectedProductDetails(null);
                        }}
                        eventData={selectedEventData}
                        orgId={orgId}
                        productDetails={selectedProductDetails || undefined}
                    />
                }

                {/* Map View - Only show when map mode is selected and map is available */}
                {showMap && viewMode === MAP_VIEW && groupedLocationsList && groupedLocationsList?.length > 0 && (
                    <MapView eventList={groupedLocationsList} orgId={orgId} />
                )}
                <Link href={getFormattedUrl(orgInfo.companyWebsite)} target="_blank" rel="noopener noreferrer">
                    <Typography variant="caption" sx={{ mt: 1, color: "#005bb5" }}>
                        {orgInfo.companyWebsite}
                    </Typography>
                </Link>
                <Typography variant="caption" sx={{ mb: 2, mt: 1 }}>
                    Traceability powered by
                    <Link href="https://tralexho.com/" target="_blank" rel="noopener noreferrer" sx={{ color: "#005bb5", textDecoration: "none" }}>
                        &nbsp;TRALEXHO
                    </Link>
                </Typography>

            </Stack>
        </ThemeProvider>
    );
}

export default TraceV2;